import React, { Component } from 'react';
import {Button, Dialog} from '@betareader/design-system';
import { WithTranslation, withTranslation } from 'react-i18next';
import { bindActionCreators, Dispatch, AnyAction } from 'redux';
import { connect } from 'react-redux';

import { blockUser, unblockUser, fetchBlockedUsers } from '../../modules/user';
import { authUtil } from '../../utils';
import AppIcon from '../AppIcon';

interface OwnProps {
  userId: string;
  displayName?: string;
  size?: 'mini' | 'tiny' | 'small' | 'medium' | 'large' | 'big' | 'huge' | 'massive';
  basic?: boolean;
  icon?: string;
  compact?: boolean;
  onBlocked?: (userId: string) => void;
}

interface StateProps {
  userProfile?: {
    _id: string;
    [key: string]: any;
  };
  idToken?: string;
  blockedUsers: (string | { _id: string; [key: string]: any })[];
}

interface DispatchProps {
  blockUser: (idToken: string, userId: string, currentUserId: string, callback?: (success: boolean) => void) => void;
  unblockUser: (idToken: string, userId: string, currentUserId: string, callback?: (success: boolean) => void) => void;
  fetchBlockedUsers: (idToken: string, userId: string) => void;
}

type Props = OwnProps & StateProps & DispatchProps & WithTranslation;

interface State {
  showConfirm: boolean;
}

export class BlockUserButton extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      showConfirm: false,
    };
  }

  componentDidMount() {
    const { userProfile, idToken } = this.props;
    // Fetch blocked users to check if this user is already blocked
    if (userProfile && userProfile._id && idToken) {
      this.props.fetchBlockedUsers(idToken, userProfile._id);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { userProfile, idToken, userId, blockedUsers } = this.props;
    // Refresh blocked users if user profile changed
    if (
      userProfile &&
      userProfile._id &&
      idToken &&
      (!prevProps.userProfile || prevProps.userProfile._id !== userProfile._id)
    ) {
      this.props.fetchBlockedUsers(idToken, userProfile._id);
    }
    // Fetch if userId changed (viewing a different user's profile)
    if (
      userId &&
      userId !== prevProps.userId &&
      userProfile &&
      userProfile._id &&
      idToken
    ) {
      this.props.fetchBlockedUsers(idToken, userProfile._id);
    }
    // Also fetch if blockedUsers is empty/undefined but we have a userProfile (initial load)
    if (
      userProfile &&
      userProfile._id &&
      idToken &&
      (blockedUsers === undefined ||
        (Array.isArray(blockedUsers) &&
          blockedUsers.length === 0 &&
          prevProps.blockedUsers === undefined))
    ) {
      this.props.fetchBlockedUsers(idToken, userProfile._id);
    }
  }

  isUserBlocked = (): boolean => {
    const { userId, blockedUsers } = this.props;
    if (!blockedUsers || !userId || !Array.isArray(blockedUsers)) return false;
    // Handle both cases: array of IDs (strings) or array of user objects
    return blockedUsers.some(user => {
      if (typeof user === 'string') {
        return user === userId;
      }
      if (user && typeof user === 'object' && '_id' in user) {
        return user._id === userId;
      }
      return false;
    });
  };

  handleBlockClick = (): void => {
    this.setState({ showConfirm: true });
  };

  handleBlockConfirm = async (): Promise<void> => {
    const { userId, userProfile, onBlocked } = this.props;

    if (!userId || !userProfile) {
      return;
    }

    const isBlocked = this.isUserBlocked();
    this.setState({ showConfirm: false });
    const idToken = await authUtil.getFreshIdToken();

    if (isBlocked) {
      // Unblock user
      this.props.unblockUser(idToken, userId, userProfile._id, success => {
        if (success && onBlocked) {
          onBlocked(userId);
        }
      });
    } else {
      // Block user
      this.props.blockUser(idToken, userId, userProfile._id, success => {
        if (success && onBlocked) {
          onBlocked(userId);
        }
      });
    }
  };

  handleBlockCancel = (): void => {
    this.setState({ showConfirm: false });
  };

  render() {
    const {
      userId,
      displayName,
      t,
      userProfile,
      size,
      basic,
      icon,
    } = this.props;
    const { showConfirm } = this.state;

    // Don't show block button for yourself
    if (userProfile && userProfile._id === userId) {
      return null;
    }

    const isBlocked = this.isUserBlocked();
    const userName = displayName || t('UnknownUser');
    const buttonText = isBlocked ? t('UnblockUser') : t('BlockUser');
    const buttonIcon = icon || (isBlocked ? 'check' : 'ban');
    const confirmContent = isBlocked
      ? t('UnblockUserConfirmation', { _name: userName })
      : t('BlockUserConfirmation', { _name: userName });
    const buttonSize = size === 'mini' || size === 'tiny' || size === 'small'
      ? 'small' : 'medium';
    const variant = basic ? 'ghost' : isBlocked ? 'secondary' : 'danger';

    return (
      <>
        <Button size={buttonSize} variant={variant}
          style={basic ? {color: isBlocked ? 'var(--br-accent)' : 'var(--br-danger)'} : undefined}
          onClick={this.handleBlockClick}>
          <AppIcon name={buttonIcon} aria-hidden='true' /> {buttonText}
        </Button>
        <Dialog open={showConfirm} title={buttonText} onClose={this.handleBlockCancel}
          actions={<>
            <Button variant='secondary' onClick={this.handleBlockCancel}>{t('Cancel')}</Button>
            <Button variant={isBlocked ? 'secondary' : 'danger'}
              onClick={this.handleBlockConfirm}>{buttonText}</Button>
          </>}>
          <p>{confirmContent}</p>
        </Dialog>
      </>
    );
  }
}

const mapStateToProps = (state: any): StateProps => ({
  userProfile: state.user.userProfile,
  idToken: state.user.idToken,
  blockedUsers: state.user.blockedUsers || [],
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): DispatchProps =>
  bindActionCreators(
    {
      blockUser,
      unblockUser,
      fetchBlockedUsers,
    },
    dispatch
  );

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(BlockUserButton)
);

